/**
 * KİLİT — `KILITLI`. Parolayla açılır; parola unutulduysa 24 kelimeyle açılır
 * ve AYNI anda yeni parola yazılır (Worker `acIfade`).
 *
 * ÇOKLU PROFİL (Adım 2): birden çok profil varsa önce profil seçilir (liste
 * herkese görünür; içerik yalnızca o profilin parolasıyla). "Yeni profil
 * ekle" kurulum ekranına götürür.
 */

import { M } from "../../dil";
import { useEffect, useState } from "react";
import type { LocalRepository, ProfilOzeti } from "@parafon/contracts/local-api";
import { useMutation } from "../../repo/useAsync";
import { kilittenSonraDonemiSifirla } from "../../lib/grafikTercihleri";
import { acilistaGizlemeyiUygula } from "../../lib/tutarGizleme";
import { beklemeMesaji, kilitDenemeSayaci, yanlisParolaMi } from "../../lib/denemeSiniri";
import { bugunYerelIso } from "../../lib/tarihDonemleri";
import { Button, Field, InlineError, TextArea, TextInput } from "../../ui/primitives";
import { ParolaAlanlari, parolaSorunu, type ParolaDegeri } from "./ParolaAlanlari";

export function KilitEkrani({
  repo,
  onAcildi,
  onYeniProfil,
}: {
  readonly repo: LocalRepository;
  readonly onAcildi: () => void;
  /** Verilirse "Yeni profil ekle" görünür. */
  readonly onYeniProfil?: () => void;
}) {
  const kilit = repo.kilit;
  const [profiller, setProfiller] = useState<readonly ProfilOzeti[]>([]);
  const [secili, setSecili] = useState<string | null>(null);
  useEffect(() => {
    void kilit?.durum().then(
      (d) => {
        setProfiller(d.profiller);
        setSecili((s) => s ?? d.profiller[0]?.id ?? null);
      },
      () => undefined,
    );
  }, [kilit]);
  const coklu = profiller.length > 1;
  const profilId = coklu ? (secili ?? undefined) : undefined;
  const [kip, setKip] = useState<"parola" | "ifade">("parola");
  const [parola, setParola] = useState("");
  const [ifade, setIfade] = useState("");
  const [yeni, setYeni] = useState<ParolaDegeri>({ parola: "", tekrar: "" });
  const [sorun, setSorun] = useState<string | null>(null);
  const run = useMutation();
  if (kilit === undefined) return null;

  /** Deneme sınırı: beklenecek süre varsa denemez; yanlışı sayar, doğruda sıfırlar. */
  const sinirla = async (dene: () => Promise<void>): Promise<void> => {
    const kalan = kilitDenemeSayaci.kalanMs();
    if (kalan > 0) throw new Error(beklemeMesaji(kalan));
    try {
      await dene();
    } catch (e: unknown) {
      if (yanlisParolaMi(e)) kilitDenemeSayaci.yanlis();
      throw e;
    }
    kilitDenemeSayaci.dogru();
  };

  const onAc = (): void => {
    void run.run(async () => {
      await sinirla(() => kilit.ac(parola, profilId));
      setParola("");
      kilittenSonraDonemiSifirla(bugunYerelIso());
      acilistaGizlemeyiUygula();
      onAcildi();
    });
  };

  const onIfadeyleAc = (): void => {
    // Parola sıfırlanırken 12 karakter ŞART (kullanıcı kararı 18.10.2026).
    const s = parolaSorunu(yeni, true);
    setSorun(s);
    if (s !== null) return;
    void run.run(async () => {
      await sinirla(() => kilit.acIfade(ifade.split(/\s+/).filter((w) => w.length > 0), yeni.parola, profilId));
      setIfade("");
      setYeni({ parola: "", tekrar: "" });
      kilittenSonraDonemiSifirla(bugunYerelIso());
      acilistaGizlemeyiUygula();
      onAcildi();
    });
  };

  return (
    <main className="mx-auto flex min-h-dvh max-w-xl flex-col justify-center p-6">
      <div className="pf-card space-y-4 p-5">
        <h1 className="text-[18px] font-semibold text-ink">{M.kilit.ekran.baslik}</h1>
        {coklu && (
          <fieldset className="grid gap-1">
            <legend className="mb-1 text-[13px] font-semibold text-ink">{M.kilit.profil}</legend>
            {profiller.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-[13px] text-ink">
                <input
                  type="radio"
                  name="kilit-profil"
                  checked={secili === p.id}
                  onChange={() => {
                    setSecili(p.id);
                    run.clearError();
                  }}
                />
                {p.etiket}
              </label>
            ))}
          </fieldset>
        )}
        {kip === "parola" ? (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              onAc();
            }}
          >
            <Field label={M.kilit.parola} htmlFor="kilit-ac-parola">
              <TextInput
                id="kilit-ac-parola"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={parola}
                onChange={(e) => setParola(e.currentTarget.value)}
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" type="submit" disabled={run.pending || parola.length === 0}>
                {run.pending ? M.kilit.ekran.aciliyor : M.kilit.ekran.ac}
              </Button>
              <Button
                onClick={() => {
                  setKip("ifade");
                  run.clearError();
                }}
              >
                {M.kilit.ekran.parolamiUnuttum}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-[13px] text-ink">{M.kilit.ekran.ifadeAciklama}</p>
            <Field label={M.kilit.kurtarmaIfadesi} htmlFor="kilit-ifade">
              <TextArea
                id="kilit-ifade"
                rows={4}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                value={ifade}
                onChange={(e) => setIfade(e.currentTarget.value)}
              />
            </Field>
            <ParolaAlanlari deger={yeni} onDegis={setYeni} etiket={M.kilit.yeniParola} kimlik="kilit-yeni-parola" sart />
            {sorun !== null && <InlineError error={new Error(sorun)} />}
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" disabled={run.pending} onClick={onIfadeyleAc}>
                {run.pending ? M.kilit.ekran.aciliyor : M.kilit.ekran.ifadeyleAc}
              </Button>
              <Button onClick={() => setKip("parola")}>{M.kilit.ekran.parolaylaAc}</Button>
            </div>
          </div>
        )}
        {run.error !== undefined && <InlineError error={run.error} />}
        {onYeniProfil !== undefined && (
          <div className="border-t border-line pt-3">
            <Button variant="ghost" onClick={onYeniProfil}>
              {M.kilit.yeniProfilEkle}
            </Button>
            <p className="mt-1 text-[12px] text-muted">{M.kilit.ekran.yeniProfilAciklama}</p>
          </div>
        )}
      </div>
    </main>
  );
}
